import { useCallback } from 'react';
import type { Participant, Room } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { roomInfoStore } from 'state/store';
import { selectRoomId } from 'state/selectors';
import { MODERATOR_ACTIONS } from 'useCase/moderation/constants';
import { publishModerationCommand } from 'useCase/moderation/commands/publishCommand';
import { isGuestParticipant } from 'useCase/moderation/status/moderators';
import { useUserSettingsContext } from 'contexts';
import { findParticipantBySid } from 'useCase/moderation/utils/findParticipantBySid';
import { assignModeratorToCallApi, getRoomNameAndWorkspaceId } from 'useCase/moderation/utils/moderatorApi';
import { getParticipantUserIdFromMetadata } from 'useCase/participant/useParticipantMetadata';
import { useParticipantItem } from 'features/ParticipantItem';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import type { ModerationConfirmState } from '../types';

interface UseModerationConfirmActionsProps {
    confirm: ModerationConfirmState;
    room: Room | null;
    localParticipant: Participant | null;
    onClose: () => void;
}

/**
 * Хук для выполнения действий подтверждения модерации
 */
export const useModerationConfirmActions = ({
    confirm,
    room,
    localParticipant,
    onClose,
}: UseModerationConfirmActionsProps) => {
    const { setAudioEnabled, setVideoEnabled } = useUserSettingsContext();
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { getDisplayData } = useParticipantItem();
    const { getTokenAndWssUrl } = useConnectToRoom();

    const onConfirm = useCallback(async () => {
        if (!confirm || !room || !localParticipant) return;

        const localSid = localParticipant.sid;
        if (!localSid) {
            console.warn('[Moderation] не удалось применить действие: отсутствует SID локального участника');
            return;
        }

        // Проверяем, что целевой участник еще в комнате
        const targetParticipant = findParticipantBySid(room, confirm.targetSid);
        if (!targetParticipant) {
            // участник уже покинул встречу то показываем снекбар модератору
            const errorMessage = t('web_videoconference_action_not_completed_user_left');
            showSnackbar(errorMessage);
            onClose();
            return;
        }

        try {
            let published: boolean;

            const targetPayload = { sid: confirm.targetSid, identity: targetParticipant.identity ?? undefined };

            switch (confirm.kind) {
                case 'MUTE_OTHERS_AUDIO': {
                    published = publishModerationCommand(room, MODERATOR_ACTIONS.MUTE_ALL_EXCEPT, targetPayload);
                    if (published && confirm.targetSid !== localSid) {
                        room.localParticipant.setMicrophoneEnabled(false);
                        setAudioEnabled(false);
                    }
                    break;
                }
                case 'MUTE_OTHERS_VIDEO': {
                    published = publishModerationCommand(
                        room,
                        MODERATOR_ACTIONS.DISABLE_VIDEO_ALL_EXCEPT,
                        targetPayload,
                    );
                    if (published && confirm.targetSid !== localSid) {
                        room.localParticipant.setCameraEnabled(false);
                        setVideoEnabled(false);
                    }
                    break;
                }
                case 'KICK': {
                    published = publishModerationCommand(room, MODERATOR_ACTIONS.REMOVE_FROM_CALL, targetPayload);
                    if (published) {
                        const targetName = getDisplayData(targetParticipant, false).name;
                        showSnackbar(t('web_videoconference_moderator_snack_removed_user', { '0': targetName }));
                    }
                    break;
                }
                case 'MAKE_MODERATOR': {
                    published = publishModerationCommand(room, MODERATOR_ACTIONS.MAKE_MODERATOR, targetPayload);
                    if (published) {
                        const targetUserId = getParticipantUserIdFromMetadata(targetParticipant.metadata);
                        const targetIdentity = targetParticipant.identity ?? null;

                        const { roomName, workspaceId } = getRoomNameAndWorkspaceId();

                        if (!roomName) {
                            console.warn('[Moderation] assignModeratorToCall не отправлен: нет roomName.');
                            showSnackbar(t('web_videoconference_error'));
                            break;
                        }

                        const tokenResult = await getTokenAndWssUrl(
                            selectRoomId(),
                            localParticipant.name ?? localParticipant.identity ?? '',
                        );
                        const payload = tokenResult?.payload;
                        const jwtToken = payload && 'jwtToken' in payload ? payload.jwtToken ?? null : null;
                        const isLocalGuest = isGuestParticipant(localParticipant);
                        const result = await assignModeratorToCallApi({
                            roomName,
                            workspaceId,
                            targetIdentity,
                            targetUserId,
                            jwtToken,
                            isLocalGuest,
                        });

                        if (result.success) {
                            roomInfoStore.addModeratorToMetadata(targetUserId, targetIdentity);
                        } else {
                            showSnackbar(t('web_videoconference_error'));
                        }
                    }
                    break;
                }
                default:
                    published = true;
            }

            if (!published) {
                showSnackbar(t('web_videoconference_error'));
            }
            onClose();
        } catch {
            showSnackbar(t('web_videoconference_error'));
            onClose();
        }
    }, [
        confirm,
        room,
        localParticipant,
        setAudioEnabled,
        setVideoEnabled,
        onClose,
        t,
        showSnackbar,
        getDisplayData,
        getTokenAndWssUrl,
    ]);

    return {
        onConfirm,
    };
};
